import React from 'react';
import iosShareIcon from '@/assets/img/iosShareIcon.png';
import { CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { differenceInDays } from 'date-fns';

const getInstallPromptLastSeenAt = (promptName: string): string =>
  localStorage.getItem(promptName) ?? '';

const setInstallPromptSeenToday = (promptName: string): void => {
  const today = new Date().toISOString();
  localStorage.setItem(promptName, today);
};

function getUserShouldBePromptedToInstall(
  promptName: string,
  daysToWaitBeforePromptingAgain: number
): boolean {
  const lastPrompt = new Date(getInstallPromptLastSeenAt(promptName));
  const daysSinceLastPrompt = differenceInDays(new Date(), lastPrompt);
  return (
    isNaN(daysSinceLastPrompt) ||
    daysSinceLastPrompt > daysToWaitBeforePromptingAgain
  );
}

const useShouldShowPrompt = (
  promptName: string,
  daysToWaitBeforePromptingAgain = 30
): [boolean, () => void] => {
  const [userShouldBePromptedToInstall, setUserShouldBePromptedToInstall] =
    React.useState(
      getUserShouldBePromptedToInstall(
        promptName,
        daysToWaitBeforePromptingAgain
      )
    );

  const handleUserSeeingInstallPrompt = () => {
    setUserShouldBePromptedToInstall(false);
    setInstallPromptSeenToday(promptName);
  };

  return [userShouldBePromptedToInstall, handleUserSeeingInstallPrompt];
};

const iosInstallPromptedAt = 'iosInstallPromptedAt';

const isIOS = (): boolean => {
  // @ts-ignore
  if (navigator.standalone) {
    //user has already installed the app
    return false;
  }
  const ua = window.navigator.userAgent;
  const isIPad = !!ua.match(/iPad/i);
  const isIPhone = !!ua.match(/iPhone/i);
  return isIPad || isIPhone;
};

const useIosInstallPrompt = (): [boolean, () => void] => {
  const [userShouldBePromptedToInstall, handleUserSeeingInstallPrompt] =
    useShouldShowPrompt(iosInstallPromptedAt);

  return [
    isIOS() && userShouldBePromptedToInstall,
    handleUserSeeingInstallPrompt,
  ];
};

const webInstallPromptedAt = 'webInstallPromptedAt';

const useWebInstallPrompt = (): [
  BeforeInstallPromptEvent | undefined,
  boolean,
  () => void,
  () => void,
] => {
  const [installPromptEvent, setInstallPromptEvent] =
    React.useState<BeforeInstallPromptEvent>();
  const [userShouldBePromptedToInstall, handleUserSeeingInstallPrompt] =
    useShouldShowPrompt(webInstallPromptedAt);

  React.useEffect(() => {
    const beforeInstallPromptHandler = (event: BeforeInstallPromptEvent) => {
      event.preventDefault();

      // // check if user has already been asked
      // if (userShouldBePromptedToInstall) {
      //   // store the event for later use
      //   setInstallPromptEvent(event);
      // }
      // Always prompt to install
      setInstallPromptEvent(event);
    };
    window.addEventListener(
      'beforeinstallprompt' as any,
      beforeInstallPromptHandler
    );
    return () =>
      window.removeEventListener(
        'beforeinstallprompt' as any,
        beforeInstallPromptHandler
      );
  }, [userShouldBePromptedToInstall]);

  const handleInstallDeclined = () => {
    handleUserSeeingInstallPrompt();
  };

  const handleInstallAccepted = () => {
    // show native prompt
    installPromptEvent!.prompt();

    // decide what to do after the user chooses
    installPromptEvent!.userChoice.then((choice) => {
      // if the user declined, we don't want to show the prompt again
      if (choice.outcome !== 'accepted') {
        handleUserSeeingInstallPrompt();
      } else {
        toast.success(
          'Bilderit fue instalada con éxito. Ya podés acceder desde tus aplicaciones.'
        );
        setInstallPromptEvent(undefined);
      }
    });
  };
  return [
    installPromptEvent,
    userShouldBePromptedToInstall,
    handleInstallDeclined,
    handleInstallAccepted,
  ];
};

/**
 * The BeforeInstallPromptEvent is fired at the Window.onbeforeinstallprompt handler
 * before a user is prompted to "install" a web site to a home screen on mobile.
 *
 * @deprecated Only supported on Chrome and Android Webview.
 */
interface BeforeInstallPromptEvent extends Event {
  /**
   * Returns an array of DOMString items containing the platforms on which the event was dispatched.
   * This is provided for user agents that want to present a choice of versions to the user such as,
   * for example, "web" or "play" which would allow the user to chose between a web version or
   * an Android version.
   */
  readonly platforms: Array<string>;

  /**
   * Returns a Promise that resolves to a DOMString containing either "accepted" or "dismissed".
   */
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;

  /**
   * Allows a developer to show the install prompt at a time of their own choosing.
   * This method returns a Promise.
   */
  prompt(): Promise<void>;
}

const InstallContext = React.createContext<{
  canPrompt: boolean;
  shouldPrompt: boolean | 'ios';
  acceptInstall: () => void;
  declineInstall: () => void;
}>({
  canPrompt: false,
  shouldPrompt: false,
  acceptInstall: () => {},
  declineInstall: () => {},
});

export const useInstallPrompt = () => React.useContext(InstallContext);
export const InstallProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [iosShouldPrompt, handleIOSInstallSeen] = useIosInstallPrompt();
  const [
    webInstallPrompt,
    webShouldPrompt,
    handleWebInstallDeclined,
    handleWebInstallAccepted,
  ] = useWebInstallPrompt();

  const installContext = React.useMemo(
    () => ({
      canPrompt: !!webInstallPrompt || isIOS(),
      shouldPrompt: iosShouldPrompt
        ? ('ios' as const)
        : webShouldPrompt
          ? webShouldPrompt
          : false,
      acceptInstall: () => {
        handleIOSInstallSeen();
        handleWebInstallAccepted();
      },
      declineInstall: () => {
        handleIOSInstallSeen();
        handleWebInstallDeclined();
      },
    }),
    [
      handleIOSInstallSeen,
      handleWebInstallAccepted,
      handleWebInstallDeclined,
      iosShouldPrompt,
      webInstallPrompt,
      webShouldPrompt,
    ]
  );

  return (
    <InstallContext.Provider value={installContext}>
      {children}
    </InstallContext.Provider>
  );
};

type InstallPormptModalProps = {
  shouldPrompt: 'ios' | boolean;
  acceptInstall: () => void;
  declineInstall: () => void;
};

export const InstallPromptModal: React.FC<InstallPormptModalProps> = ({
  shouldPrompt,
  acceptInstall,
  declineInstall,
}) => {
  if (!shouldPrompt) {
    return null;
  }
  return (
    <Dialog open>
      <DialogContent>
        <div
          className="mx-auto bg-card"
          style={{
            borderTopRightRadius: '50%',
            borderTopLeftRadius: '50%',
            // backgroundColor: '#fff',
            marginTop: '-50px',
            width: '120px',
            height: '120px',
            padding: '20px',
          }}
        >
          <img
            width="80px"
            src={import.meta.env.BASE_URL + '/android-chrome-192x192.png'}
            alt="Icon"
          />
        </div>
        <CardTitle className="text-center" style={{ marginBottom: '20px' }}>
          <h3>Instalá Bilderit</h3>
        </CardTitle>
        <hr />
        {shouldPrompt === 'ios' ? (
          <>
            <p className="text-center">
              Presioná
              <img
                src={iosShareIcon}
                className="inline-block m-2 mt-[0]"
                alt="Compartir"
                width="20"
              />{' '}
              junto a la barra de dirección de tu navegador, luego &quot;Añadir
              a la pantalla de inicio&quot;
            </p>
            <hr />

            <div className="flex justify-center">
              <Button
                onClick={() => {
                  declineInstall();
                }}
              >
                Cerrar
              </Button>
            </div>
          </>
        ) : (
          <div className="flex justify-center gap-4">
            <Button variant="secondary" onClick={declineInstall}>
              Cerrar
            </Button>
            <Button onClick={acceptInstall}>Instalar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
